import type {
    SketchDimensionState,
    SolverIslandGraphConstraintLink,
    SolverIslandGraphDimensionLink,
    SolverIslandGraphEntityLink,
} from '../../../../history/solver';
import {
    isAbstractSketchEdgeObject,
    isSketchMirrorAxisReferenceProvider,
    isSketchGroup,
    isSketchObject,
    isSketchPointObject,
    isSketchSlotObject,
} from '../../../../mesh/guards';
import type {AbstractSketchEdgeObject, ISketchObject, SketchPointObject} from '../../../../mesh/sketch';
import type {SketchRelationDefinition} from '../relationDefinition';
import type {
    SketchSolverSnapshotSupplement,
    SketchSolverSnapshotSupplementProvider,
} from './SketchSolverRuntimeTypes';

/**
 * 运行时 Object3D 最小协议。
 */
interface RuntimeObjectLike {
    /** Three.js 运行时 UUID。 */
    readonly uuid?: string;
    /** 父对象。 */
    readonly parent?: RuntimeObjectLike | null;
    /** 子对象。 */
    readonly children?: readonly unknown[];
}

/**
 * 可调用方法读取结果。
 */
type MethodResult<TValue> = TValue | readonly TValue[] | ReadonlySet<TValue> | null | undefined;

/**
 * 草图 solver 运行时图采集输入。
 */
export interface CollectSketchSolverRuntimeGraphInput {
    /** 采集入口对象。 */
    readonly seedObjects: readonly unknown[];
    /** 补充状态 provider。 */
    readonly supplementProvider?: SketchSolverSnapshotSupplementProvider;
}

/**
 * 草图 solver 运行时图。
 *
 * 设计说明：
 * 1. Map 中保存运行时对象，只在采集期间使用，不写入 history；
 * 2. links 与 dimensionStates 是纯数据边界，供后续生成可持久化 snapshot；
 * 3. seedEntityIds / seedPointIds 只表达直接触发源，真正影响范围由 island 图遍历解析。
 */
export interface SketchSolverRuntimeGraph {
    /** 草图 ID。 */
    readonly sketchId: string;
    /** 草图定义修订号。 */
    readonly sketchRevision?: number;
    /** seed 实体 ID。 */
    readonly seedEntityIds: readonly string[];
    /** seed 点 ID。 */
    readonly seedPointIds: readonly string[];
    /** 实体到点的连接边。 */
    readonly entities: readonly SolverIslandGraphEntityLink[];
    /** 约束连接边。 */
    readonly constraints: readonly SolverIslandGraphConstraintLink[];
    /** 尺寸连接边。 */
    readonly dimensions: readonly SolverIslandGraphDimensionLink[];
    /** 尺寸状态。 */
    readonly dimensionStates: readonly SketchDimensionState[];
    /** provider 返回的补充状态。 */
    readonly supplement?: SketchSolverSnapshotSupplement;
    /** 实体运行时对象。 */
    readonly entityObjects: ReadonlyMap<string, ISketchObject>;
    /** 外部稳定引用目标；按 persistentId 索引，但不属于 island 实体。 */
    readonly referenceObjects: ReadonlyMap<string, ISketchObject>;
    /** 点运行时对象。 */
    readonly pointObjects: ReadonlyMap<string, SketchPointObject>;
    /** 约束运行时对象。 */
    readonly constraintObjects: ReadonlyMap<string, SketchRelationDefinition>;
}

/**
 * 采集过程中的可变图。
 */
interface MutableSketchSolverRuntimeGraph {
    /** 实体运行时对象。 */
    readonly entityObjects: Map<string, ISketchObject>;
    /** 外部稳定引用目标；只供关系恢复，不参与实体/边快照。 */
    readonly referenceObjects: Map<string, ISketchObject>;
    /** 点运行时对象。 */
    readonly pointObjects: Map<string, SketchPointObject>;
    /** 约束运行时对象。 */
    readonly constraintObjects: Map<string, SketchRelationDefinition>;
    /** seed 实体 ID。 */
    readonly seedEntityIds: Set<string>;
    /** seed 点 ID。 */
    readonly seedPointIds: Set<string>;
}

/**
 * 读取运行时对象 UUID。
 *
 * @param value 待读取对象。
 * @returns UUID；不是运行时对象时返回 null。
 */
function readRuntimeUuid(value: unknown): string | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const uuid = Reflect.get(value, 'uuid');
    return typeof uuid === 'string' && uuid.length > 0 ? uuid : null;
}

/**
 * 判断值是否为运行时对象。
 *
 * @param value 待判断值。
 * @returns 是否具备最小 Object3D 协议。
 */
function isRuntimeObjectLike(value: unknown): value is RuntimeObjectLike {
    return Boolean(readRuntimeUuid(value));
}

/**
 * 稳定排序并去重。
 *
 * @param values 原始字符串列表。
 * @returns 排序后的去重列表。
 */
function sortUnique(values: readonly string[]): string[] {
    return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
}

/**
 * 读取对象上的无参方法。
 *
 * @param target 目标对象。
 * @param methodName 方法名。
 * @returns 方法执行结果；方法不存在时返回 undefined。
 */
function callOptionalMethod<TValue>(target: unknown, methodName: string): MethodResult<TValue> {
    if (!target || typeof target !== 'object') {
        return undefined;
    }

    const method = Reflect.get(target, methodName);
    if (typeof method !== 'function') {
        return undefined;
    }

    return method.call(target) as MethodResult<TValue>;
}

/**
 * 将单值、数组或 Set 转换成数组。
 *
 * @param value 方法返回值。
 * @returns 标准数组。
 */
function normalizeMethodResult<TValue>(value: MethodResult<TValue>): TValue[] {
    if (value === null || value === undefined) {
        return [];
    }

    if (Array.isArray(value)) {
        return [...value];
    }

    if (value instanceof Set) {
        return [...value.values()];
    }

    return [value as TValue];
}

/**
 * 读取对象子级。
 *
 * @param value 待读取对象。
 * @returns 子对象数组。
 */
function readChildren(value: unknown): readonly unknown[] {
    if (!value || typeof value !== 'object') {
        return [];
    }

    const children = Reflect.get(value, 'children');
    return Array.isArray(children) ? children : [];
}

/**
 * 读取对象父级。
 *
 * @param value 待读取对象。
 * @returns 父对象。
 */
function readParent(value: unknown): RuntimeObjectLike | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const parent = Reflect.get(value, 'parent');
    return isRuntimeObjectLike(parent) ? parent : null;
}

/**
 * 解析约束历史 ID。
 *
 * @param constraint 约束对象。
 * @returns 约束历史 ID。
 * @throws 当关系缺少跨会话稳定标识时抛出错误。
 */
export function resolveSketchRelationDefinitionHistoryId(constraint: SketchRelationDefinition): string {
    const persistentId = constraint.persistentId;
    if (typeof persistentId === 'string' && persistentId.length > 0) {
        return persistentId;
    }

    throw new Error(`草图关系 ${constraint.id} 缺少跨会话稳定标识。`);
}

/**
 * 收集对象的直接草图点。
 *
 * @param object 草图对象。
 * @returns 草图点列表。
 */
function collectDirectSketchPoints(object: unknown): SketchPointObject[] {
    const points: SketchPointObject[] = [];

    if (isSketchPointObject(object)) {
        points.push(object);
    }

    normalizeMethodResult<SketchPointObject>(callOptionalMethod(object, 'getRelatedSketchPointsSnapshot'))
        .forEach((point) => {
            if (isSketchPointObject(point)) {
                points.push(point);
            }
        });

    [
        'getCenterControlPoint',
        'getEdgeControlPoint',
        'getCenterPoint',
        'getStartPoint',
        'getEndPoint',
        'getMidPoint',
        'getEdgeStartPoint',
        'getEdgeEndPoint',
    ].forEach((methodName) => {
        normalizeMethodResult<SketchPointObject>(callOptionalMethod(object, methodName)).forEach((point) => {
            if (isSketchPointObject(point)) {
                points.push(point);
            }
        });
    });

    [
        'getJunctionPointArray',
        'getControlPoints',
        'getQuadrantPoints',
    ].forEach((methodName) => {
        normalizeMethodResult<SketchPointObject>(callOptionalMethod(object, methodName)).forEach((point) => {
            if (isSketchPointObject(point)) {
                points.push(point);
            }
        });
    });

    if (isSketchSlotObject(object)) {
        /**
         * 槽口是关系可直接选中的复合实体，solver island 必须把全部成员边控制点
         * 连在槽口实体节点上。只登记四个接合点会让中心轴、弧心等真实自由度落到
         * 另一个 island，实体级镜像随后只能拿到半截结构。
         */
        [
            object.centerlineStartPoint,
            object.centerlineEndPoint,
            object.centerPoint,
            object.arcCenterPoint,
            object.centerArcMidpoint,
        ].forEach((point) => {
            if (isSketchPointObject(point)) {
                points.push(point);
            }
        });
        object.getEdges().forEach((edge) => {
            points.push(...collectDirectSketchPoints(edge));
        });
    }

    return Array.from(new Map(points.map((point) => [point.uuid, point])).values());
}

/**
 * 收集对象的关联草图对象。
 *
 * @param object 草图对象或点对象。
 * @returns 可继续遍历的关联对象。
 */
function collectLinkedSketchObjects(object: unknown): unknown[] {
    const linkedObjects: unknown[] = [];

    normalizeMethodResult<unknown>(callOptionalMethod(object, 'getRelatedSketchObjectsSnapshot'))
        .forEach((linkedObject) => {
            linkedObjects.push(linkedObject);
        });

    normalizeMethodResult<unknown>(callOptionalMethod(object, 'getConnectedEdgesSnapshot'))
        .forEach((linkedObject) => {
            linkedObjects.push(linkedObject);
        });

    [
        'getLines',
        'getArcs',
    ].forEach((methodName) => {
        normalizeMethodResult<unknown>(callOptionalMethod(object, methodName)).forEach((linkedObject) => {
            linkedObjects.push(linkedObject);
        });
    });

    readChildren(object).forEach((child) => {
        linkedObjects.push(child);
    });

    return linkedObjects;
}

/**
 * 收集对象上的 solver 约束。
 *
 * 说明：尺寸关系展示已经迁为容器内的纯 `SketchRelationSnapshot` 派生视图，
 * `getRelations()` 只返回真实领域关系，因此本入口无需再维护尺寸镜像排除分支。
 * 统一参数求解链路直接读取对象容器并生成 plain-data 快照；对象本身不执行关系传播。
 *
 * @param object 草图对象。
 * @returns 真实领域关系列表。
 */
function collectSketchRelationDefinitions(object: unknown): SketchRelationDefinition[] {
    if (!object || typeof object !== 'object') {
        return [];
    }

    const constraints = Reflect.get(object, 'constraints');
    if (!constraints || typeof constraints !== 'object') {
        return [];
    }

    const getRelations = Reflect.get(constraints, 'getRelations');
    if (typeof getRelations !== 'function') {
        return [];
    }

    const result = getRelations.call(constraints);
    return Array.isArray(result) ? result as SketchRelationDefinition[] : [];
}

/**
 * 收集约束目标对象。
 *
 * @param constraint solver 约束。
 * @returns 约束目标对象。
 */
function collectConstraintTargets(constraint: SketchRelationDefinition): readonly unknown[] {
    const targets = constraint.getTargetsSnapshot();
    return Array.isArray(targets) ? targets : [];
}

/**
 * 从对象向上解析草图组。
 *
 * @param object 运行时对象。
 * @returns 草图组对象或 null。
 */
function resolveSketchGroupFromObject(object: unknown): RuntimeObjectLike | null {
    let current: RuntimeObjectLike | null = isRuntimeObjectLike(object) ? object : null;

    while (current) {
        if (isSketchGroup(current)) {
            return current;
        }

        current = readParent(current);
    }

    return null;
}

/**
 * 解析草图 ID 与修订号。
 *
 * @param seeds seed 对象。
 * @param graph 可变图。
 * @returns 草图身份信息。
 */
function resolveSketchIdentity(
    seeds: readonly unknown[],
    graph: MutableSketchSolverRuntimeGraph,
): {sketchId: string; sketchRevision?: number} | null {
    const candidates = [
        ...seeds,
        ...graph.entityObjects.values(),
        ...graph.pointObjects.values(),
    ];

    for (const candidate of candidates) {
        const sketchGroup = resolveSketchGroupFromObject(candidate);
        if (!sketchGroup) {
            continue;
        }

        const revision = Reflect.get(sketchGroup, 'definitionRevision');
        return {
            sketchId: String(sketchGroup.uuid),
            sketchRevision: typeof revision === 'number' ? revision : undefined,
        };
    }

    const fallbackId = candidates.map(readRuntimeUuid).find((uuid): uuid is string => typeof uuid === 'string');
    if (!fallbackId) {
        return null;
    }

    return {
        sketchId: `detached-sketch:${fallbackId}`,
        sketchRevision: undefined,
    };
}

/**
 * 将约束纳入运行时图。
 *
 * @param graph 可变图。
 * @param constraint 约束。
 * @param queue 遍历队列。
 */
function collectConstraintIntoGraph(
    graph: MutableSketchSolverRuntimeGraph,
    constraint: SketchRelationDefinition,
    queue: unknown[],
): void {
    const constraintId = resolveSketchRelationDefinitionHistoryId(constraint);
    if (graph.constraintObjects.has(constraintId)) {
        return;
    }

    graph.constraintObjects.set(constraintId, constraint);
    collectConstraintTargets(constraint).forEach((target) => {
        queue.push(target);
    });
}

/**
 * 将草图点纳入运行时图。
 *
 * @param graph 可变图。
 * @param point 草图点。
 */
function collectPointIntoGraph(
    graph: MutableSketchSolverRuntimeGraph,
    point: SketchPointObject,
): void {
    if (!graph.pointObjects.has(point.uuid)) {
        graph.pointObjects.set(point.uuid, point);
    }
}

/**
 * 将草图实体纳入运行时图。
 *
 * @param graph 可变图。
 * @param object 草图实体。
 */
function collectEntityIntoGraph(
    graph: MutableSketchSolverRuntimeGraph,
    object: ISketchObject,
): void {
    if (isSketchPointObject(object)) {
        return;
    }
    if (isSketchMirrorAxisReferenceProvider(object)) {
        graph.referenceObjects.set(object.persistentId, object);
        return;
    }

    if (!graph.entityObjects.has(object.uuid)) {
        graph.entityObjects.set(object.uuid, object);
    }
}

/**
 * 解析目标对象对应的实体与点 ID。
 *
 * @param target 约束或尺寸目标。
 * @param graph 可变图。
 * @returns 目标 ID 集合。
 */
function resolveTargetIds(
    target: unknown,
    graph: MutableSketchSolverRuntimeGraph,
): {entityIds: string[]; pointIds: string[]} {
    const entityIds: string[] = [];
    const pointIds: string[] = [];

    if (isSketchPointObject(target)) {
        pointIds.push(target.uuid);
    }

    if (
        isSketchObject(target)
        && !isSketchPointObject(target)
        && !isSketchMirrorAxisReferenceProvider(target)
    ) {
        entityIds.push(target.uuid);
    }

    collectDirectSketchPoints(target).forEach((point) => {
        pointIds.push(point.uuid);
    });

    if (isAbstractSketchEdgeObject(target)) {
        target.getRelatedSketchPointsSnapshot().forEach((point) => {
            pointIds.push(point.uuid);
        });
    }

    pointIds.forEach((pointId) => {
        graph.pointObjects.forEach((point) => {
            if (point.uuid === pointId) {
                collectPointIntoGraph(graph, point);
            }
        });
    });

    return {
        entityIds: sortUnique(entityIds),
        pointIds: sortUnique(pointIds),
    };
}

/**
 * 创建实体连接边。
 *
 * @param entity 实体对象。
 * @returns 实体连接边。
 */
function createEntityLink(entity: ISketchObject): SolverIslandGraphEntityLink {
    return {
        entityId: entity.uuid,
        pointIds: sortUnique(collectDirectSketchPoints(entity).map((point) => point.uuid)),
    };
}

/**
 * 创建约束连接边。
 *
 * @param constraintId 约束 ID。
 * @param constraint 约束对象。
 * @param graph 可变图。
 * @returns 约束连接边。
 */
function createConstraintLink(
    constraintId: string,
    constraint: SketchRelationDefinition,
    graph: MutableSketchSolverRuntimeGraph,
): SolverIslandGraphConstraintLink {
    const targetEntityIds: string[] = [];
    const targetPointIds: string[] = [];

    collectConstraintTargets(constraint).forEach((target) => {
        const targetIds = resolveTargetIds(target, graph);
        targetEntityIds.push(...targetIds.entityIds);
        targetPointIds.push(...targetIds.pointIds);
    });

    return {
        constraintId,
        entityIds: sortUnique(targetEntityIds),
        pointIds: sortUnique(targetPointIds),
    };
}

/**
 * 创建尺寸连接边。
 *
 * @param dimensionState 尺寸状态。
 * @returns 尺寸连接边。
 */
function createDimensionLink(dimensionState: SketchDimensionState): SolverIslandGraphDimensionLink {
    return {
        dimensionId: dimensionState.dimensionId,
        entityIds: sortUnique(dimensionState.targetEntityIds),
        pointIds: sortUnique(dimensionState.targetPointIds),
    };
}

/**
 * 过滤当前运行时图可验证的尺寸状态。
 *
 * @param dimensionStates 尺寸状态。
 * @param graph 可变图。
 * @returns 可纳入 snapshot 的尺寸状态。
 */
function filterKnownDimensionStates(
    dimensionStates: readonly SketchDimensionState[],
    graph: MutableSketchSolverRuntimeGraph,
): SketchDimensionState[] {
    /** 尺寸文档保存 persistentId，而 island 使用运行时 uuid；采集边界在此统一归一身份。 */
    const entityRuntimeIds = new Map<string, string>();
    const pointRuntimeIds = new Map<string, string>();

    graph.entityObjects.forEach((entity, runtimeId) => {
        entityRuntimeIds.set(runtimeId, runtimeId);
        entityRuntimeIds.set(entity.persistentId, runtimeId);
    });
    graph.pointObjects.forEach((point, runtimeId) => {
        pointRuntimeIds.set(runtimeId, runtimeId);
        pointRuntimeIds.set(point.persistentId, runtimeId);
    });

    return dimensionStates.flatMap((dimensionState) => {
        const targetEntityIds = dimensionState.targetEntityIds.map((entityId) => entityRuntimeIds.get(entityId));
        const targetPointIds = dimensionState.targetPointIds.map((pointId) => pointRuntimeIds.get(pointId));

        if (targetEntityIds.some((entityId) => !entityId) || targetPointIds.some((pointId) => !pointId)) {
            return [];
        }

        return [{
            ...dimensionState,
            targetEntityIds: sortUnique(targetEntityIds as string[]),
            targetPointIds: sortUnique(targetPointIds as string[]),
        }];
    });
}

/**
 * 执行一次运行时 island 图采集。
 *
 * @param input 采集输入。
 * @returns 可变图。
 */
function collectMutableRuntimeGraph(input: CollectSketchSolverRuntimeGraphInput): MutableSketchSolverRuntimeGraph {
    const graph: MutableSketchSolverRuntimeGraph = {
        entityObjects: new Map(),
        referenceObjects: new Map(),
        pointObjects: new Map(),
        constraintObjects: new Map(),
        seedEntityIds: new Set(),
        seedPointIds: new Set(),
    };
    const queue: unknown[] = [...input.seedObjects];
    const visitedIds = new Set<string>();

    input.seedObjects.forEach((seedObject) => {
        if (isSketchPointObject(seedObject)) {
            graph.seedPointIds.add(seedObject.uuid);
        }

        if (
            isSketchObject(seedObject)
            && !isSketchPointObject(seedObject)
            && !isSketchMirrorAxisReferenceProvider(seedObject)
        ) {
            graph.seedEntityIds.add(seedObject.uuid);
        }
    });

    while (queue.length > 0) {
        const current = queue.shift();
        const currentId = readRuntimeUuid(current);
        if (!currentId || visitedIds.has(currentId)) {
            continue;
        }

        visitedIds.add(currentId);

        if (isSketchPointObject(current)) {
            collectPointIntoGraph(graph, current);
        }

        if (isSketchObject(current)) {
            collectEntityIntoGraph(graph, current);
            collectDirectSketchPoints(current).forEach((point) => {
                collectPointIntoGraph(graph, point);
                queue.push(point);
            });
            collectSketchRelationDefinitions(current).forEach((constraint) => {
                collectConstraintIntoGraph(graph, constraint, queue);
            });
        }

        collectLinkedSketchObjects(current).forEach((linkedObject) => {
            queue.push(linkedObject);
        });
    }

    /**
     * Undo/Redo 重建发生在关系已被移除之后，此时无法再从源实体沿关系遍历到外部轴。
     * 因此外部引用必须从草图组的正式对象注册表补齐；它们仍不会进入 island entityIds。
     */
    const sketchGroups = new Map<string, RuntimeObjectLike>();
    [...input.seedObjects, ...graph.entityObjects.values(), ...graph.pointObjects.values()].forEach((object) => {
        const sketchGroup = resolveSketchGroupFromObject(object);
        const groupId = readRuntimeUuid(sketchGroup);
        if (sketchGroup && groupId) {
            sketchGroups.set(groupId, sketchGroup);
        }
    });
    sketchGroups.forEach((sketchGroup) => {
        const registeredObjects = normalizeMethodResult<unknown>(
            callOptionalMethod(sketchGroup, 'getSketchReferenceTargets'),
        );
        registeredObjects.forEach((object) => {
            if (isSketchMirrorAxisReferenceProvider(object)) {
                graph.referenceObjects.set(object.persistentId, object);
            }
        });
    });

    return graph;
}

/**
 * 收集草图 solver 运行时图。
 *
 * @param input 采集输入。
 * @returns 运行时图；没有有效草图对象时返回 null。
 */
export function collectSketchSolverRuntimeGraph(
    input: CollectSketchSolverRuntimeGraphInput,
): SketchSolverRuntimeGraph | null {
    const mutableGraph = collectMutableRuntimeGraph(input);
    const identity = resolveSketchIdentity(input.seedObjects, mutableGraph);

    if (!identity || (mutableGraph.entityObjects.size === 0 && mutableGraph.pointObjects.size === 0)) {
        return null;
    }

    const entities = Array.from(mutableGraph.entityObjects.values()).map(createEntityLink);
    const constraints = Array.from(mutableGraph.constraintObjects.entries()).map(([constraintId, constraint]) => {
        return createConstraintLink(constraintId, constraint, mutableGraph);
    });
    const baseSupplement = input.supplementProvider?.readSketchSolverSnapshotSupplement({
        sketchId: identity.sketchId,
        seedObjects: input.seedObjects,
        entityIds: sortUnique(entities.map((entity) => entity.entityId)),
        pointIds: sortUnique(Array.from(mutableGraph.pointObjects.keys())),
        constraintIds: sortUnique(constraints.map((constraint) => constraint.constraintId)),
    });
    const dimensionStates = filterKnownDimensionStates(baseSupplement?.dimensionStates ?? [], mutableGraph);
    const dimensions = dimensionStates.map(createDimensionLink);

    return {
        sketchId: identity.sketchId,
        sketchRevision: identity.sketchRevision,
        seedEntityIds: sortUnique(Array.from(mutableGraph.seedEntityIds)),
        seedPointIds: sortUnique(Array.from(mutableGraph.seedPointIds)),
        entities,
        constraints,
        dimensions,
        dimensionStates,
        supplement: baseSupplement,
        entityObjects: mutableGraph.entityObjects,
        referenceObjects: mutableGraph.referenceObjects,
        pointObjects: mutableGraph.pointObjects,
        constraintObjects: mutableGraph.constraintObjects,
    };
}

/**
 * 读取实体关联点 ID。
 *
 * @param entity 实体对象。
 * @returns 关联点 ID 列表。
 */
export function readSketchEntityPointIds(entity: ISketchObject): readonly string[] {
    return sortUnique(collectDirectSketchPoints(entity).map((point) => point.uuid));
}

/**
 * 判断实体是否是抽象草图边。
 *
 * @param value 待判断值。
 * @returns 是否为抽象草图边。
 */
export function isRuntimeSketchEdge(value: unknown): value is AbstractSketchEdgeObject {
    return isAbstractSketchEdgeObject(value);
}
